import { isInBrowser } from '../../common/utils/ssrUtils.js';

export const ACTIVE_CHAT_KEY = 'online-active-chat';
export const SURVEY_PENDING_KEY = 'online-genesys-survey-pending';
export const HAS_HUMAN_AGENT_RESPONDED_KEY = 'online-genesys-has-human-agent-responded';
export const CHAT_STATE_CHANGED_EVENT = 'chat-state-changed';

const SESSION_TTL_MS = 60 * 60 * 1000;
const SURVEY_PENDING_TTL_MS = 5 * 60 * 1000;

const notifyStateChanged = (): void => {
  if (isInBrowser()) {
    globalThis.dispatchEvent(new Event(CHAT_STATE_CHANGED_EVENT));
  }
};

const isExpired = (timestamp: number, ttlMs: number = SESSION_TTL_MS): boolean => {
  return Date.now() - timestamp > ttlMs;
};

export const hasActiveChatSession = (): boolean => {
  if (!isInBrowser()) {
    return false;
  }

  const raw = localStorage.getItem(ACTIVE_CHAT_KEY);
  if (!raw) {
    return false;
  }

  const timestamp = Number(raw);
  if (isNaN(timestamp) || isExpired(timestamp)) {
    localStorage.removeItem(ACTIVE_CHAT_KEY);
    return false;
  }

  return true;
};

export const setActiveChatSession = (): void => {
  if (!isInBrowser()) {
    return;
  }

  localStorage.setItem(ACTIVE_CHAT_KEY, String(Date.now()));
  notifyStateChanged();
};

export const isSurveyPending = (): boolean => {
  if (!isInBrowser()) {
    return false;
  }

  const raw = localStorage.getItem(SURVEY_PENDING_KEY);
  if (!raw) {
    return false;
  }

  const timestamp = Number(raw);
  if (Number.isNaN(timestamp) || isExpired(timestamp, SURVEY_PENDING_TTL_MS)) {
    localStorage.removeItem(SURVEY_PENDING_KEY);
    return false;
  }

  return true;
};

export const setSurveyPending = (pending: boolean): void => {
  if (!isInBrowser()) {
    return;
  }

  if (pending) {
    localStorage.setItem(SURVEY_PENDING_KEY, String(Date.now()));
  } else {
    localStorage.removeItem(SURVEY_PENDING_KEY);
  }

  notifyStateChanged();
};

export const clearActiveChatSession = (): void => {
  if (!isInBrowser()) {
    return;
  }

  localStorage.removeItem(ACTIVE_CHAT_KEY);
  localStorage.removeItem(SURVEY_PENDING_KEY);
  localStorage.removeItem(HAS_HUMAN_AGENT_RESPONDED_KEY);

  notifyStateChanged();
};

/**
 * Sticky, session-scoped flag: has a human agent ever responded during the
 * current chat session? Set once (via `markHumanAgentResponded()`) the first
 * time an agent-authored message is observed, and never unset by a later
 * bot-authored message.
 */
export const hasHumanAgentEverResponded = (): boolean => {
  if (!isInBrowser()) {
    return false;
  }

  const raw = localStorage.getItem(HAS_HUMAN_AGENT_RESPONDED_KEY);
  if (!raw) {
    return false;
  }

  const timestamp = Number(raw);
  if (isNaN(timestamp) || isExpired(timestamp)) {
    localStorage.removeItem(HAS_HUMAN_AGENT_RESPONDED_KEY);
    return false;
  }

  return true;
};

export const markHumanAgentResponded = (): void => {
  if (!isInBrowser()) {
    return;
  }

  // Already set (and not expired) — no-op, keep the original timestamp.
  if (hasHumanAgentEverResponded()) {
    return;
  }

  localStorage.setItem(HAS_HUMAN_AGENT_RESPONDED_KEY, String(Date.now()));
  notifyStateChanged();
};
